import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ChevronLeft,
  ChevronRight,
  FastForward,
  History,
  Info,
  Minus,
  PackageOpen,
  Plus,
  ShieldCheck,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "wouter";

import { api, ApiError, type PackPurchase } from "@/api";
import {
  Button,
  EmptyState,
  ErrorState,
  HorsePortrait,
  money,
  Panel,
  SectionHeading,
  Skeleton,
  TierStars,
} from "@/components/ui";
import { OwnershipSubnav } from "@/components/OwnershipSubnav";
import { useCapabilities } from "@/capabilities/CapabilityProvider";
import { packLabel, trackTitle } from "@/features/racing/venue";
import { useRealtimeTopic } from "@/realtime/RealtimeProvider";
import { useSession } from "@/session/SessionProvider";

import { QuickRip } from "./QuickRip";
import {
  clearForeignRecoveryState,
  clearIntent,
  clearQueue,
  isDeliveredBatch,
  isDeliveredPurchase,
  loadIntent,
  loadQueue,
  type PurchaseIntent,
  saveIntent,
  saveQueue,
  type SavedQueue,
} from "./packRecovery";

function wait(milliseconds: number) {
  return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
}

export default function PacksPage() {
  const { session, requestLogin } = useSession();
  const capabilities = useCapabilities();
  const [, navigate] = useLocation();
  const queryClient = useQueryClient();
  const playerId = session?.playerId ?? null;
  const [quantity, setQuantity] = useState(1);
  const [instant, setInstant] = useState(false);
  const [seriesCode, setSeriesCode] = useState<string | null>(null);
  const [queue, setQueue] = useState<SavedQueue | null>(null);
  const [pendingIntent, setPendingIntent] = useState<PurchaseIntent | null>(
    null,
  );
  const [deliveryMessage, setDeliveryMessage] = useState("");
  const recoveredPlayer = useRef<number | null>(null);
  const autoResumeStarted = useRef(false);
  useRealtimeTopic("world.packs");
  useRealtimeTopic("world.market");

  const series = useQuery({
    queryKey: ["packs", "series"],
    queryFn: api.packSeries,
    refetchInterval: 30_000,
  });
  const liveOpens = useQuery({
    queryKey: ["live-opens", 20],
    queryFn: () => api.liveOpens(20),
    refetchInterval: 15_000,
  });
  const history = useQuery({
    queryKey: ["packs", "history", session?.playerId],
    queryFn: () => api.packHistory(),
    enabled: Boolean(session),
    refetchInterval: 30_000,
  });

  // Recovery state is loaded once per authenticated player. Another
  // account's (or the legacy global) state is discarded, never inherited.
  useEffect(() => {
    if (playerId === null || recoveredPlayer.current === playerId) return;
    recoveredPlayer.current = playerId;
    autoResumeStarted.current = false;
    clearForeignRecoveryState(playerId);
    setQueue(loadQueue(playerId));
    setPendingIntent(loadIntent(playerId));
  }, [playerId]);

  // Keep an unfinished reveal restorable across reloads; anything else is
  // consumed (closing the overlay or finishing the last summary removes it).
  useEffect(() => {
    if (playerId === null) return;
    if (queue?.purchases.every(isDeliveredPurchase)) {
      saveQueue(playerId, queue);
    }
  }, [playerId, queue]);

  function persistIntent(intent: PurchaseIntent | null) {
    setPendingIntent(intent);
    if (playerId === null) return;
    if (intent) {
      saveIntent(playerId, intent);
    } else {
      clearIntent(playerId);
    }
  }

  function consumeQueue() {
    setQueue(null);
    if (playerId !== null) clearQueue(playerId);
  }

  const openSeries = (series.data?.series ?? []).filter(
    (row) => (row.available ?? row.horsesPerPack) >= row.horsesPerPack,
  );
  const seriesIndex = Math.max(
    0,
    openSeries.findIndex((row) => row.code === seriesCode),
  );
  const selected = openSeries[seriesIndex];
  const activeCode = selected?.code ?? null;
  const fairness = useQuery({
    queryKey: ["packs", "fairness", activeCode],
    queryFn: () => api.packFairness(activeCode),
    enabled: Boolean(activeCode),
    staleTime: 30_000,
  });

  function stepSeries(step: number) {
    if (!openSeries.length) return;
    const next = (seriesIndex + step + openSeries.length) % openSeries.length;
    setSeriesCode(openSeries[next].code);
  }

  const purchase = useMutation({
    mutationFn: async (intent: PurchaseIntent) => {
      setDeliveryMessage("Authorizing pack purchase…");
      for (let attempt = 0; attempt < 90; attempt += 1) {
        const data = await api.purchasePack(
          intent.code,
          {
            clientRef: intent.clientRef,
            quantity: intent.quantity,
          },
          intent.clientRef,
        );
        if (isDeliveredBatch(data)) return data;
        setDeliveryMessage(
          data.purchases.some((item) => item.status === "reserved")
            ? "Payment authorized — delivering horses…"
            : "Confirming payment with the casino…",
        );
        await wait(Math.min(3_000, 600 + attempt * 150));
      }
      throw new Error(
        "Your purchase is still reconciling. It will resume automatically.",
      );
    },
    onSuccess: (data, intent) => {
      if (!isDeliveredBatch(data)) return;
      // Delivery consumes the idempotency key unconditionally: the next
      // "Open pack" click must mint a fresh clientRef and a fresh batch.
      persistIntent(null);
      setDeliveryMessage("");
      setQueue({
        purchases: data.purchases,
        index: 0,
        instant: intent.instant,
      });
      void queryClient.invalidateQueries({ queryKey: ["packs"] });
      void queryClient.invalidateQueries({ queryKey: ["live-opens"] });
      void queryClient.invalidateQueries({ queryKey: ["stable"] });
      void queryClient.invalidateQueries({ queryKey: ["balance"] });
    },
    onError: (error) => {
      setDeliveryMessage("");
      if (
        error instanceof ApiError &&
        [400, 402, 403, 409].includes(error.status)
      ) {
        persistIntent(null);
      }
    },
  });

  // A pending (202) purchase resumes automatically with its ORIGINAL key.
  useEffect(() => {
    if (
      !pendingIntent ||
      !session ||
      series.isPending ||
      purchase.isPending ||
      autoResumeStarted.current
    ) {
      return;
    }
    autoResumeStarted.current = true;
    purchase.mutate(pendingIntent);
  }, [pendingIntent, purchase, series.isPending, session]);

  function buy() {
    if (!session) {
      requestLogin();
      return;
    }
    if (!capabilities.can("packs_ownership")) return;
    if (!selected || purchase.isPending) return;
    // An explicit attempt owns this intent; only recovered intents auto-resume.
    autoResumeStarted.current = true;
    if (pendingIntent) {
      // Explicit resume of a still-reconciling purchase: same key.
      purchase.mutate(pendingIntent);
      return;
    }
    consumeQueue();
    const intent: PurchaseIntent = {
      intent: `${selected.code}:${quantity}`,
      clientRef: crypto.randomUUID(),
      code: selected.code,
      quantity,
      instant,
    };
    persistIntent(intent);
    purchase.mutate(intent);
  }

  const probabilities = useMemo(
    () => Object.entries(selected?.tierProbabilities ?? {}),
    [selected],
  );

  return (
    <div className="page page--packs">
      <OwnershipSubnav />

      <Panel className="pack-opening-stage">
        {series.error ? (
          <>
            {series.data ? (
              <p className="form-note">Showing the last loaded pack series.</p>
            ) : null}
            <ErrorState
              error={series.error}
              retry={() => void series.refetch()}
            />
          </>
        ) : null}
        {series.isPending ? (
          <Skeleton variant="pack-stage" />
        ) : !series.data ? null : !selected ? (
          <EmptyState title="No open pack series" />
        ) : (
          <>
            <header className="pack-opening-stage__header">
              <button
                aria-label="Previous series"
                disabled={openSeries.length < 2}
                onClick={() => stepSeries(-1)}
                type="button"
              >
                <ChevronLeft aria-hidden />
              </button>
              <div>
                <strong>
                  {selected.kind === "repack"
                    ? "Sealed Re-Pack"
                    : "Standard Pack"}
                </strong>
                <span>
                  {selected.horsesPerPack} random racehorses ·{" "}
                  {trackTitle(selected)}
                  {openSeries.length > 1
                    ? ` · ${seriesIndex + 1}/${openSeries.length}`
                    : ""}
                </span>
              </div>
              <button
                aria-label="Next series"
                disabled={openSeries.length < 2}
                onClick={() => stepSeries(1)}
                type="button"
              >
                <ChevronRight aria-hidden />
              </button>
            </header>

            <div
              className="pack-preview-cards"
              aria-label="Five sealed horse slots"
            >
              {Array.from({ length: selected.horsesPerPack }, (_, index) => (
                <div className="pack-preview-card" key={index}>
                  <div>
                    <span>HW</span>
                    <strong>Sealed horse</strong>
                    <small>Slot {index + 1}</small>
                  </div>
                </div>
              ))}
            </div>

            <div className="pack-purchase-controls">
              <label className="fast-open-toggle">
                <input
                  checked={instant}
                  onChange={(event) => setInstant(event.target.checked)}
                  type="checkbox"
                />
                <FastForward aria-hidden size={14} />
                Instant open
              </label>
              <div className="quantity-stepper">
                <button
                  aria-label="Decrease quantity"
                  disabled={quantity <= 1}
                  onClick={() => setQuantity((value) => Math.max(1, value - 1))}
                  type="button"
                >
                  <Minus aria-hidden />
                </button>
                <strong>{quantity}</strong>
                <button
                  aria-label="Increase quantity"
                  disabled={quantity >= 10}
                  onClick={() =>
                    setQuantity((value) => Math.min(10, value + 1))
                  }
                  type="button"
                >
                  <Plus aria-hidden />
                </button>
              </div>
              <Button
                disabled={
                  purchase.isPending ||
                  (Boolean(session) && !capabilities.can("packs_ownership"))
                }
                onClick={buy}
              >
                {purchase.isPending
                  ? deliveryMessage || "Delivering…"
                  : pendingIntent
                    ? "Resume pending purchase"
                    : `Open ${quantity === 1 ? "pack" : `${quantity} packs`} · ${money(
                        selected.priceCents * quantity,
                      )}`}
              </Button>
              {pendingIntent && !purchase.isPending ? (
                <p className="form-note">
                  This purchase is saved and will resume with the same
                  idempotency key.
                </p>
              ) : null}
              {purchase.error ? (
                <p className="form-error" role="alert">
                  {purchase.error.message}
                </p>
              ) : null}
              {session && !capabilities.can("packs_ownership") ? (
                <p className="form-error">
                  {capabilities.reason("packs_ownership")}
                </p>
              ) : null}
            </div>

            <div className="pack-probabilities">
              {probabilities.map(([tier, probability]) => (
                <span
                  className={`tier-probability tier-probability--${tier}`}
                  key={tier}
                >
                  <TierStars tier={tier} />
                  {(probability * 100).toFixed(0)}%
                </span>
              ))}
            </div>

            <details className="pack-fairness">
              <summary>
                <ShieldCheck aria-hidden />
                Fairness
              </summary>
              {fairness.error ? (
                <>
                  {fairness.data ? (
                    <p className="form-note">
                      Showing the last loaded fairness details.
                    </p>
                  ) : null}
                  <ErrorState
                    error={fairness.error}
                    retry={() => void fairness.refetch()}
                  />
                </>
              ) : null}
              {fairness.data ? (
                <div>
                  <code>{fairness.data.commitHash}</code>
                  <p>{fairness.data.explanation}</p>
                  <span>
                    {fairness.data.drawn} drawn · {fairness.data.available}{" "}
                    available ·{" "}
                    {fairness.data.seedRevealed
                      ? "seed revealed"
                      : "seed sealed"}
                  </span>
                </div>
              ) : fairness.isPending ? (
                <Skeleton rows={2} variant="table" />
              ) : null}
              {series.data?.tierDisclosure?.length ? (
                <div className="tier-disclosure">
                  <strong>What owners actually earn (modelled)</strong>
                  <table>
                    <thead>
                      <tr>
                        <th>Tier</th>
                        <th>Chance</th>
                        <th>Instant cash</th>
                        <th>30d income</th>
                        <th>Career income</th>
                        <th>Payback</th>
                        <th>Best exit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {series.data.tierDisclosure.map((row) => (
                        <tr key={row.tier}>
                          <td>
                            <span
                              className={`tier-chip tier-chip--${row.tier}`}
                            >
                              {row.tier}
                            </span>
                          </td>
                          <td>{(row.chance * 100).toFixed(0)}%</td>
                          <td>
                            {row.instantQuoteCents != null
                              ? money(row.instantQuoteCents)
                              : "—"}
                          </td>
                          <td>{money(row.income30dCents)}</td>
                          <td>{money(row.careerIncomeCents)}</td>
                          <td>{row.paybackDays}d</td>
                          <td>{row.bestExitX.toFixed(2)}x</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <small>{series.data.tierDisclosureBasis}</small>
                </div>
              ) : null}
            </details>
          </>
        )}
      </Panel>

      <SectionHeading icon={<Info aria-hidden />} title="Live Opens" />
      {liveOpens.error ? (
        <Panel aria-label="Live opens">
          {liveOpens.data ? (
            <p className="form-note">Showing the last loaded live opens.</p>
          ) : null}
          <ErrorState
            error={liveOpens.error}
            retry={() => void liveOpens.refetch()}
          />
        </Panel>
      ) : null}
      {liveOpens.isPending ? (
        <Skeleton rows={4} variant="activity" />
      ) : liveOpens.data?.opens.length ? (
        <div className="live-opens-rail">
          {liveOpens.data.opens.map((open) => (
            <article key={open.positionId}>
              {open.topHorse ? (
                <HorsePortrait compact horse={open.topHorse} />
              ) : null}
              <div>
                <strong>{open.topPull ?? "Pack opened"}</strong>
                <span>
                  {open.displayName} · {packLabel(open)}
                </span>
              </div>
              <TierStars
                tier={open.topHorse?.tier ?? open.tiers.at(-1) ?? "common"}
              />
            </article>
          ))}
        </div>
      ) : liveOpens.data ? (
        <EmptyState title="No live opens yet" />
      ) : null}

      <SectionHeading icon={<History aria-hidden />} title="Pack History" />
      <Panel className="pack-history" aria-label="Pack history">
        {session && history.error ? (
          <>
            {history.data ? (
              <p className="form-note">Showing the last loaded pack history.</p>
            ) : null}
            <ErrorState
              error={history.error}
              retry={() => void history.refetch()}
            />
          </>
        ) : null}
        {!session ? (
          <EmptyState
            action={<Button onClick={requestLogin}>Sign in</Button>}
            title="Sign in to see pack history"
          />
        ) : history.isPending ? (
          <Skeleton rows={6} variant="table" />
        ) : history.data?.purchases.length ? (
          <table>
            <thead>
              <tr>
                <th>Pack</th>
                <th>Price</th>
                <th>Opened</th>
                <th>Horses</th>
                <th>Best pull</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {history.data.purchases.map((pack) => {
                const top = maxHorse(pack);
                return (
                  <tr key={pack.positionId}>
                    <td>
                      <PackageOpen aria-hidden />
                      <span>
                        <strong>{packLabel(pack)}</strong>
                        <small>5 racehorses</small>
                      </span>
                    </td>
                    <td>{money(pack.priceCents)}</td>
                    <td>
                      {pack.deliveredAt
                        ? new Date(pack.deliveredAt).toLocaleString()
                        : "—"}
                    </td>
                    <td>{pack.contents.length}</td>
                    <td>{top?.name ?? "—"}</td>
                    <td>
                      {isDeliveredPurchase(pack) ? (
                        <button
                          onClick={() =>
                            setQueue({
                              purchases: [pack],
                              index: 0,
                              instant: false,
                            })
                          }
                          type="button"
                        >
                          Replay →
                        </button>
                      ) : (
                        <span>{pack.status}</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : history.data ? (
          <EmptyState title="No packs opened yet" />
        ) : null}
      </Panel>

      {queue?.purchases.length
        ? createPortal(
            <div
              aria-label="Quick Rip pack opening"
              aria-modal="true"
              className="quick-rip-overlay"
              role="dialog"
            >
              <QuickRip
                key={queue.purchases[0].positionId}
                initialIndex={queue.index}
                instant={queue.instant}
                onBuyAnother={() => {
                  consumeQueue();
                  buy();
                }}
                onClose={consumeQueue}
                onFinished={() => {
                  // The last summary was seen: a reload must not replay it.
                  if (playerId !== null) clearQueue(playerId);
                }}
                onIndexChange={(index) =>
                  setQueue((current) =>
                    current ? { ...current, index } : null,
                  )
                }
                onViewStable={() => {
                  consumeQueue();
                  navigate("/app/player");
                }}
                purchases={queue.purchases}
                repeatPriceCents={
                  selected ? selected.priceCents * quantity : null
                }
              />
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}

function maxHorse(pack: PackPurchase) {
  return pack.contents.reduce(
    (best, horse) =>
      !best || horse.stickerCents > best.stickerCents ? horse : best,
    null as PackPurchase["contents"][number] | null,
  );
}
